import { motion } from 'framer-motion';
import { Gift, Trophy, CheckCircle, Sparkles, Star } from 'lucide-react';
import { Prize } from '@/hooks/usePrizes';
import { format } from 'date-fns';

interface YearlyPrizeCalendarProps {
  prizes: Prize[];
  year: number;
  onSelectMonth: (monthYear: string) => void;
  selectedMonth: string;
}

const MONTHS = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
];

export function YearlyPrizeCalendar({ 
  prizes, 
  year, 
  onSelectMonth, 
  selectedMonth 
}: YearlyPrizeCalendarProps) {
  const getMonthPrize = (monthIndex: number) => {
    const monthYear = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
    return prizes.find(p => p.month_year === monthYear);
  };

  const getMonthStatus = (monthIndex: number) => {
    const prize = getMonthPrize(monthIndex);
    const monthYear = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
    const currentMonthYear = format(new Date(), 'yyyy-MM');
    
    if (!prize) return 'empty';
    if (monthYear === currentMonthYear && prize.status === 'pending') return 'current';
    if (prize.status === 'claimed') return 'claimed';
    if (prize.status === 'claimable') return 'claimable';
    // Check if it's a custom prize or default
    if (prize.isDefault) return 'default';
    return 'custom';
  };

  const customCount = prizes.filter(p => !p.isDefault && p.month_year.startsWith(year.toString())).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-zinc-400">
          Calendario {year}
        </h3>
        <span className="text-xs text-zinc-500">
          {customCount} personalizados
        </span>
      </div>

      <div className="grid grid-cols-6 gap-2">
        {MONTHS.map((month, index) => {
          const monthYear = `${year}-${String(index + 1).padStart(2, '0')}`;
          const status = getMonthStatus(index);
          const prize = getMonthPrize(index);
          const isSelected = selectedMonth === monthYear;
          const isCustom = prize && !prize.isDefault;

          return (
            <motion.button
              key={monthYear}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onSelectMonth(monthYear)}
              className={`
                relative p-2 rounded-lg text-center transition-all
                ${isSelected 
                  ? 'bg-amber-500/30 border-2 border-amber-500' 
                  : isCustom
                    ? 'bg-purple-500/20 border border-purple-500/50 hover:border-purple-500'
                    : 'bg-zinc-800/50 border border-zinc-700 hover:border-zinc-600'
                }
              `}
            >
              <span className={`text-xs font-medium ${
                isSelected ? 'text-amber-300' : isCustom ? 'text-purple-300' : 'text-zinc-300'
              }`}>
                {month}
              </span>
              
              <div className="mt-1 flex justify-center">
                {status === 'empty' && (
                  <div className="w-4 h-4 rounded-full bg-zinc-700/50" />
                )}
                {status === 'default' && (
                  <Gift className="w-4 h-4 text-zinc-500" />
                )}
                {status === 'custom' && (
                  <Star className="w-4 h-4 text-purple-400" />
                )}
                {status === 'current' && (
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Sparkles className="w-4 h-4 text-amber-400" />
                  </motion.div>
                )}
                {status === 'claimable' && (
                  <motion.div
                    animate={{ rotate: [0, -10, 10, 0] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  >
                    <Gift className="w-4 h-4 text-yellow-400" />
                  </motion.div>
                )}
                {status === 'claimed' && (
                  <CheckCircle className="w-4 h-4 text-green-400" />
                )}
              </div>

              {prize && prize.winner_id && (
                <div className="absolute -top-1 -right-1">
                  <Trophy className="w-3 h-3 text-amber-400" />
                </div>
              )}
              
              {isCustom && !prize.winner_id && (
                <div className="absolute -top-1 -right-1">
                  <div className="w-2 h-2 rounded-full bg-purple-500" />
                </div>
              )}
            </motion.button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-zinc-500 pt-2 border-t border-zinc-800">
        <div className="flex items-center gap-1">
          <Sparkles className="w-3 h-3 text-amber-400" />
          <span>En curso</span>
        </div>
        <div className="flex items-center gap-1">
          <Star className="w-3 h-3 text-purple-400" />
          <span>Personalizado</span>
        </div>
        <div className="flex items-center gap-1">
          <Gift className="w-3 h-3 text-yellow-400" />
          <span>Por reclamar</span>
        </div>
        <div className="flex items-center gap-1">
          <CheckCircle className="w-3 h-3 text-green-400" />
          <span>Entregado</span>
        </div>
        <div className="flex items-center gap-1">
          <Gift className="w-3 h-3 text-zinc-500" />
          <span>Por defecto</span>
        </div>
      </div>
    </div>
  );
}